import { useState, useEffect, useMemo } from 'react';
import { Save, Copy, Upload as UploadIcon, Trash2, GripVertical, Clock, Eye, EyeOff, Plus, X, FileJson, AlertTriangle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { templatesApi, assetsApi, singularApi } from '@/lib/api';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import AssetLibrary from './AssetLibrary';
import DeleteTemplateDialog from './DeleteTemplateDialog';
import PublishPreviewDialog from './PublishPreviewDialog';

interface TemplateEditorProps {
  facilityId: string;
  isAdmin: boolean;
}

interface TemplateAsset {
  id: number;
  assetId: number;
  duration: number;
  sortOrder: number;
  isActive: boolean;
  asset: {
    id: number;
    originalName: string;
    filename: string;
    s3Url: string;
    thumbnailUrl?: string;
    width: number;
    height: number;
    mimeType: string;
    fileSize: number;
  };
}


export default function TemplateEditor({ facilityId, isAdmin }: TemplateEditorProps) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check for templateId in URL params
  const urlTemplateId = searchParams.get('templateId');

  // State
  const [currentTemplateId, setCurrentTemplateId] = useState<number | null>(
    urlTemplateId ? parseInt(urlTemplateId) : null
  );
  const [templateAssets, setTemplateAssets] = useState<TemplateAsset[]>([]);
  const [originalTemplateAssets, setOriginalTemplateAssets] = useState<TemplateAsset[]>([]);
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPublishPreview, setShowPublishPreview] = useState(false);
  const [deleteDialogData, setDeleteDialogData] = useState<any>(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [publishTargets, setPublishTargets] = useState<any[]>([]);
  const [publishSuccess, setPublishSuccess] = useState<any>(null);

  // Fetch templates list
  const { data: templatesData } = useQuery({
    queryKey: ['templates', facilityId],
    queryFn: () => templatesApi.list({ facilityId, limit: 100 }),
    enabled: !!facilityId,
  });

  const templates = templatesData?.templates || [];

  // Fetch current template details
  const { data: currentTemplate } = useQuery({
    queryKey: ['template', currentTemplateId],
    queryFn: () => templatesApi.getById(currentTemplateId!),
    enabled: !!currentTemplateId,
  });

  // Initialize with URL template, default template, or first template
  useEffect(() => {
    // If we have a template ID from URL, it's already set in state, so skip auto-loading
    if (urlTemplateId) {
      // Clear the URL param after loading
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('templateId');
      setSearchParams(newParams, { replace: true });
      return;
    }

    // Otherwise, auto-load default or first template
    if (templates.length > 0 && currentTemplateId === null) {
      const defaultTemplate = templates.find((t) => t.isDefault);
      const templateToLoad = defaultTemplate ?? templates[0];
      if (templateToLoad) setCurrentTemplateId(templateToLoad.id);
    }
  }, [templates, currentTemplateId, urlTemplateId, searchParams, setSearchParams]);

  // Update templateAssets when currentTemplate changes
  useEffect(() => {
    if (currentTemplate) {
      const sortedAssets = [...currentTemplate.templateAssets].sort((a, b) => a.sortOrder - b.sortOrder);
      setTemplateAssets(sortedAssets as unknown as TemplateAsset[]);
      setOriginalTemplateAssets(JSON.parse(JSON.stringify(sortedAssets)));
    }
  }, [currentTemplate]);

  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(templateAssets) !== JSON.stringify(originalTemplateAssets);
  }, [templateAssets, originalTemplateAssets]);

  // Use unsaved changes hook
  useUnsavedChanges(hasUnsavedChanges);

  // Create template mutation
  const createTemplateMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; facilityId: string; assets: any[] }) =>
      templatesApi.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setCurrentTemplateId(data.id);
      setShowSaveAsDialog(false);
      setNewTemplateName('');
      setNewTemplateDescription('');
    },
  });

  // Update template assets mutation
  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, assets }: { id: number; assets: any[] }) =>
      templatesApi.updateAssets(id, { assets }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template', currentTemplateId] });
    },
  });

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: async ({ id, deleteAssets }: { id: number; deleteAssets: boolean }): Promise<void> => {
      // If deleteAssets is true, we need to delete each exclusive asset
      if (deleteAssets && deleteDialogData?.exclusiveAssets) {
        await Promise.all([
          templatesApi.delete(id),
          ...deleteDialogData.exclusiveAssets.map((asset: any) =>
            assetsApi.delete(asset.assetId, true)
          ),
        ]);
        return;
      }
      await templatesApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['asset-library'] });
      setShowDeleteDialog(false);
      setDeleteDialogData(null);
      // Load first available template
      if (templates.length > 1) {
        const nextTemplate = templates.find((t) => t.id !== currentTemplateId);
        if (nextTemplate) {
          setCurrentTemplateId(nextTemplate.id);
        }
      } else {
        setCurrentTemplateId(null);
        setTemplateAssets([]);
        setOriginalTemplateAssets([]);
      }
    },
  });

  // Publish template mutation
  const publishMutation = useMutation({
    mutationFn: ({ facilityId, templateId }: { facilityId: string; templateId: number }) =>
      singularApi.publishSlideshow(facilityId, templateId),
    onSuccess: (data) => {
      setShowPublishPreview(false);
      console.log('Publish response:', data);

      // Check for UI overwrite risk in any of the attempts
      const hasOverwriteRisk = data.attempts?.some((a: any) => a.uiOverwriteRisk);

      // Check for verification failures
      const verificationFailures = data.attempts?.filter((a: any) => a.success && a.verifiedMatch === false);

      if (data.status === 'success') {
        setPublishSuccess({ data, hasOverwriteRisk, verificationFailures });
        let message = `Published successfully to ${data.successCount} sheet(s)!`;
        if (verificationFailures && verificationFailures.length > 0) {
          message += `\n\n⚠️ Warning: ${verificationFailures.length} sheet(s) returned HTTP 200 but verification failed. Data may not be stored correctly.`;
        }
        alert(message);
      } else if (data.status === 'partial') {
        setPublishSuccess({ data, hasOverwriteRisk, verificationFailures });
        alert(`Partially published: ${data.successCount} succeeded, ${data.failureCount} failed.\n\nCheck console for details.`);
        console.error('Failed attempts:', data.attempts.filter((a: any) => !a.success));
      } else {
        alert(`Publish failed. Please check the console logs for details.`);
        console.error('Publish failed:', data);
      }
    },
    onError: (error: any) => {
      setShowPublishPreview(false);
      console.error('Publish error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`Failed to publish: ${errorMessage}\n\nCheck console for details.`);
    },
  });

  const handleTemplateSelect = (templateId: number) => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Do you want to discard them and switch templates?'
      );
      if (!confirmed) return;
    }
    setCurrentTemplateId(templateId);
  };

  const handleCreateNewTemplate = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Do you want to discard them and create a new template?'
      );
      if (!confirmed) return;
    }
    setCurrentTemplateId(null);
    setTemplateAssets([]);
    setOriginalTemplateAssets([]);
  };

  const handleSave = () => {
    if (!currentTemplateId) {
      // New template - show save as dialog
      setShowSaveAsDialog(true);
      return;
    }

    // Update existing template
    const assetsData = templateAssets.map((ta, index) => ({
      assetId: ta.assetId,
      duration: ta.duration,
      sortOrder: index,
      isActive: ta.isActive,
    }));

    updateTemplateMutation.mutate({
      id: currentTemplateId,
      assets: assetsData,
    });
  };

  const handleSaveAs = () => {
    setShowSaveAsDialog(true);
  };

  const handleSaveAsConfirm = () => {
    if (!newTemplateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    const assetsData = templateAssets.map((ta, index) => ({
      assetId: ta.assetId,
      duration: ta.duration,
      sortOrder: index,
      isActive: ta.isActive,
    }));

    createTemplateMutation.mutate({
      name: newTemplateName,
      ...(newTemplateDescription ? { description: newTemplateDescription } : {}),
      facilityId,
      assets: assetsData,
    });
  };

  // Build slideshow items for Singular.live preview
  const buildSlideshowItems = () => {
    return templateAssets
      .filter((ta) => ta.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((ta) => ({
        duration: ta.duration,
        payload: {
          Sponsor: ta.asset.s3Url,
        },
      }));
  };

  const handlePublishPreview = async () => {
    if (!currentTemplateId) {
      alert('Please save the template before publishing');
      return;
    }
    if (hasUnsavedChanges) {
      alert('Please save your changes before publishing');
      return;
    }

    // Fetch publish targets to show UI overwrite risk warning
    try {
      const targetsResponse = await singularApi.getPublishTargets(facilityId);
      setPublishTargets(targetsResponse.targets || []);
      setShowPublishPreview(true);
    } catch (error) {
      console.error('Failed to fetch publish targets:', error);
      alert('Failed to load publish targets. Please try again.');
    }
  };

  const handlePublish = () => {
    if (!currentTemplateId) return;
    publishMutation.mutate({ facilityId, templateId: currentTemplateId });
  };

  const handleDeleteTemplate = async () => {
    if (!currentTemplateId) return;

    try {
      const data = await templatesApi.getAssetsUsedElsewhere(currentTemplateId);
      setDeleteDialogData(data);
      setShowDeleteDialog(true);
    } catch (error) {
      console.error('Error fetching asset usage:', error);
      alert('Failed to check asset usage. Please try again.');
    }
  };

  const handleDeleteConfirm = (deleteAssets: boolean) => {
    if (!currentTemplateId) return;
    deleteTemplateMutation.mutate({ id: currentTemplateId, deleteAssets });
  };

  const handleAddAsset = (assetId: number) => {
    // Check if asset already in template
    if (templateAssets.some((ta) => ta.assetId === assetId)) {
      alert('This asset is already in the template');
      return;
    }

    // Fetch asset details
    assetsApi.getById(assetId).then((asset) => {
      const newTemplateAsset: TemplateAsset = {
        id: Date.now(), // Temporary ID
        assetId: asset.id,
        duration: 8, // Default duration
        sortOrder: templateAssets.length,
        isActive: true,
        asset: {
          id: asset.id,
          originalName: asset.originalName,
          filename: asset.filename,
          s3Url: asset.s3Url,
          ...(asset.thumbnailUrl !== undefined ? { thumbnailUrl: asset.thumbnailUrl } : {}),
          width: asset.width ?? 0,
          height: asset.height ?? 0,
          mimeType: asset.mimeType,
          fileSize: asset.fileSize,
        },
      };
      setTemplateAssets([...templateAssets, newTemplateAsset]);
    });
  };

  const handleRemoveAsset = (index: number) => {
    const confirmed = window.confirm('Remove this asset from the template?');
    if (!confirmed) return;
    setTemplateAssets(templateAssets.filter((_, i) => i !== index));
  };

  const handleDurationChange = (index: number, duration: number) => {
    const updated = [...templateAssets];
    updated[index]!.duration = duration;
    setTemplateAssets(updated);
  };

  const handleActiveToggle = (index: number) => {
    const updated = [...templateAssets];
    updated[index]!.isActive = !updated[index]!.isActive;
    setTemplateAssets(updated);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updated = [...templateAssets];
    const draggedItem = updated[draggedIndex]!;
    updated.splice(draggedIndex, 1);
    updated.splice(index, 0, draggedItem);
    setTemplateAssets(updated);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const totalDuration = useMemo(() => {
    return templateAssets
      .filter((ta) => ta.isActive)
      .reduce((sum, ta) => sum + ta.duration, 0);
  }, [templateAssets]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header / Template Selector Bar */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Template Editor
            </h3>
            {currentTemplateId ? (
              <div className="flex items-center gap-2">
                <select
                  value={currentTemplateId}
                  onChange={(e) => handleTemplateSelect(Number(e.target.value))}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                >
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} {template.isDefault ? '(Default)' : ''}
                    </option>
                  ))}
                </select>
                {hasUnsavedChanges && (
                  <Badge variant="destructive" className="text-xs">
                    Unsaved Changes
                  </Badge>
                )}
              </div>
            ) : (
              <Badge variant="outline" className="text-xs">
                New Template
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCreateNewTemplate}
              className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <Plus className="h-4 w-4 mr-1" />
              New
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSave}
              disabled={!hasUnsavedChanges && !!currentTemplateId}
              className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4 mr-1" />
              Save
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveAs}
              className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <Copy className="h-4 w-4 mr-1" />
              Save As
            </Button>
            {currentTemplateId && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeleteTemplate}
                className="bg-white dark:bg-gray-700 text-red-600 dark:text-red-400 border-gray-300 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handlePublishPreview}
              disabled={!currentTemplateId || hasUnsavedChanges || templateAssets.length === 0}
              className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileJson className="h-4 w-4 mr-1" />
              Preview JSON
            </Button>
            <Button
              size="sm"
              onClick={handlePublishPreview}
              disabled={!currentTemplateId || hasUnsavedChanges || templateAssets.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UploadIcon className="h-4 w-4 mr-1" />
              Publish
            </Button>
          </div>
        </div>

        {/* Template Info */}
        {currentTemplate && (
          <div className="text-xs text-gray-600 dark:text-gray-400">
            <span>Last saved: {new Date(currentTemplate.updatedAt).toLocaleString()}</span>
            <span className="mx-2">•</span>
            <span>{templateAssets.length} assets</span>
            <span className="mx-2">•</span>
            <span>Total duration: {formatDuration(totalDuration)}</span>
          </div>
        )}

        {/* Post-Publish Warning Banner */}
        {publishSuccess && publishSuccess.hasOverwriteRisk && (
          <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex gap-3 items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-200 mb-1">
                  Important: Refresh Singular Control App UI
                </h3>
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  If any Singular Control App pages are open in other tabs/windows, <strong>refresh them now</strong> and <strong>do not click the Update button</strong>, or they may overwrite the data you just published.
                </p>
                <button
                  onClick={() => setPublishSuccess(null)}
                  className="mt-2 text-xs text-yellow-700 dark:text-yellow-400 hover:text-yellow-900 dark:hover:text-yellow-200 underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Asset Library Sidebar */}
        {showAssetLibrary && (
          <div className="w-96 border-r border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="h-full flex flex-col">
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex items-center justify-between">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100">Asset Library</h4>
                <button
                  onClick={() => setShowAssetLibrary(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                <AssetLibrary
                  facilityId={facilityId}
                  onAddToTemplate={handleAddAsset}
                  assetsInTemplate={templateAssets.map((ta) => ta.assetId)}
                  showAddButton={true}
                  allowUpload={isAdmin}
                />
              </div>
            </div>
          </div>
        )}

        {/* Template Content Area */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 p-4">
          {templateAssets.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Plus className="h-12 w-12 text-gray-400" />
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {currentTemplateId ? 'This template has no assets' : 'Start building your template'}
                </p>
                <Button
                  onClick={() => setShowAssetLibrary(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Assets from Library
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 max-w-4xl mx-auto">
              {!showAssetLibrary && (
                <div className="mb-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowAssetLibrary(true)}
                    className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add More Assets
                  </Button>
                </div>
              )}

              {templateAssets.map((ta, index) => (
                <div
                  key={ta.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-4 p-4 rounded-lg border ${
                    ta.isActive
                      ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                      : 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 opacity-60'
                  } hover:shadow-md transition-all cursor-move`}
                >
                  {/* Drag Handle */}
                  <div className="flex-shrink-0 text-gray-400 cursor-grab active:cursor-grabbing">
                    <GripVertical className="h-5 w-5" />
                  </div>

                  {/* Thumbnail */}
                  <div className="w-24 h-24 flex-shrink-0 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                    <img
                      src={ta.asset.thumbnailUrl || ta.asset.s3Url}
                      alt={ta.asset.originalName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>

                  {/* Asset Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                      {ta.asset.originalName}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {ta.asset.width}×{ta.asset.height}
                    </p>
                  </div>

                  {/* Duration Control */}
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={ta.duration}
                      onChange={(e) => handleDurationChange(index, Number(e.target.value))}
                      className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400">sec</span>
                  </div>

                  {/* Active Toggle */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleActiveToggle(index)}
                    className={`bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 ${
                      ta.isActive ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {ta.isActive ? (
                      <Eye className="h-4 w-4" />
                    ) : (
                      <EyeOff className="h-4 w-4" />
                    )}
                  </Button>

                  {/* Remove Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveAsset(index)}
                    className="bg-white dark:bg-gray-700 text-red-600 dark:text-red-400 border-gray-300 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Save As Dialog */}
      {showSaveAsDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Save Template As
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter template name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={newTemplateDescription}
                  onChange={(e) => setNewTemplateDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional description"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSaveAsDialog(false);
                  setNewTemplateName('');
                  setNewTemplateDescription('');
                }}
                className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveAsConfirm}
                disabled={createTemplateMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {createTemplateMutation.isPending ? 'Saving...' : 'Save Template'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Preview Dialog */}
      {showPublishPreview && currentTemplate && (
        <PublishPreviewDialog
          templateName={currentTemplate.name}
          slideshowItems={buildSlideshowItems()}
          targets={publishTargets}
          onPublish={handlePublish}
          onCancel={() => setShowPublishPreview(false)}
          isPublishing={publishMutation.isPending}
        />
      )}

      {/* Delete Template Dialog */}
      {showDeleteDialog && deleteDialogData && currentTemplate && (
        <DeleteTemplateDialog
          templateName={currentTemplate.name}
          sharedAssets={deleteDialogData.sharedAssets}
          exclusiveAssets={deleteDialogData.exclusiveAssets}
          onConfirm={handleDeleteConfirm}
          onCancel={() => {
            setShowDeleteDialog(false);
            setDeleteDialogData(null);
          }}
          isDeleting={deleteTemplateMutation.isPending}
        />
      )}
    </div>
  );
}
